<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BoardsAPI from 'kanban/api/boards';
import SettingsAPI from 'kanban/api/settings';

const { t } = useI18n();
const router = useRouter();

const cooldownHours = ref(24);
const followupHours = ref(24);
const timezone = ref('');
const businessDays = ref([]);
const startHour = ref(9);
const endHour = ref(18);
const pausedStepIds = ref([]);
const boardSteps = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);

const days = computed(() => [
  { value: 0, label: t('KANBAN.SETTINGS_PAGE.DAYS.SUNDAY') },
  { value: 1, label: t('KANBAN.SETTINGS_PAGE.DAYS.MONDAY') },
  { value: 2, label: t('KANBAN.SETTINGS_PAGE.DAYS.TUESDAY') },
  { value: 3, label: t('KANBAN.SETTINGS_PAGE.DAYS.WEDNESDAY') },
  { value: 4, label: t('KANBAN.SETTINGS_PAGE.DAYS.THURSDAY') },
  { value: 5, label: t('KANBAN.SETTINGS_PAGE.DAYS.FRIDAY') },
  { value: 6, label: t('KANBAN.SETTINGS_PAGE.DAYS.SATURDAY') },
]);

const loadSettings = async () => {
  try {
    const { data } = await SettingsAPI.get();
    const settings = data.settings || {};
    cooldownHours.value = settings.cooldown_hours ?? 24;
    followupHours.value = settings.followup_hours ?? 24;
    const schedule = settings.business_schedule;
    timezone.value = schedule?.timezone || '';
    businessDays.value = [...(schedule?.business_days || [])];
    startHour.value = schedule?.start_hour ?? 9;
    endHour.value = schedule?.end_hour ?? 18;
    pausedStepIds.value = [...(settings.paused_step_ids || [])];
    const boardsResponse = await BoardsAPI.get();
    boardSteps.value = (boardsResponse.data.boards || []).flatMap(
      board => board.steps_summary || board.steps || []
    );
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t('KANBAN.SETTINGS_PAGE.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    const businessSchedule = timezone.value.trim()
      ? {
          timezone: timezone.value.trim(),
          business_days: [...businessDays.value].sort((a, b) => a - b),
          start_hour: Number(startHour.value),
          end_hour: Number(endHour.value),
        }
      : null;
    await SettingsAPI.update({
      cooldown_hours: Number(cooldownHours.value),
      followup_hours: Number(followupHours.value),
      business_schedule: businessSchedule,
      paused_step_ids: [...pausedStepIds.value],
    });
    useAlert(t('KANBAN.SETTINGS_PAGE.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t('KANBAN.SETTINGS_PAGE.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const toggleDay = day => {
  businessDays.value = businessDays.value.includes(day)
    ? businessDays.value.filter(value => value !== day)
    : [...businessDays.value, day];
};

const togglePausedStep = stepId => {
  pausedStepIds.value = pausedStepIds.value.includes(stepId)
    ? pausedStepIds.value.filter(id => id !== stepId)
    : [...pausedStepIds.value, stepId];
};

onMounted(loadSettings);
</script>

<template>
  <SettingsLayout :is-loading="isLoading" class="w-full max-w-2xl">
    <template #header>
      <div class="flex items-center justify-between w-full">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('KANBAN.SETTINGS_PAGE.TITLE') }}
        </h1>
        <Button variant="ghost" size="sm" @click="router.back()">
          {{ t('KANBAN.SETTINGS_PAGE.BACK') }}
        </Button>
      </div>
    </template>
    <div class="flex flex-col gap-6 p-6">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-model="cooldownHours"
          type="number"
          min="1"
          max="2160"
          :label="t('KANBAN.SETTINGS_PAGE.COOLDOWN_HOURS')"
        />
        <Input
          v-model="followupHours"
          type="number"
          min="1"
          max="2160"
          :label="t('KANBAN.SETTINGS_PAGE.FOLLOWUP_HOURS')"
        />
      </div>
      <div class="flex flex-col gap-3">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.SETTINGS_PAGE.SCHEDULE_TITLE') }}
        </h2>
        <Input
          v-model="timezone"
          :label="t('KANBAN.SETTINGS_PAGE.TIMEZONE')"
          :placeholder="t('KANBAN.SETTINGS_PAGE.TIMEZONE_PLACEHOLDER')"
        />
        <div class="grid grid-cols-2 gap-4">
          <Input
            v-model="startHour"
            type="number"
            min="0"
            max="23"
            :label="t('KANBAN.SETTINGS_PAGE.START_HOUR')"
          />
          <Input
            v-model="endHour"
            type="number"
            min="1"
            max="23"
            :label="t('KANBAN.SETTINGS_PAGE.END_HOUR')"
          />
        </div>
        <div class="flex flex-wrap gap-2">
          <label
            v-for="day in days"
            :key="day.value"
            class="flex items-center gap-1 text-xs text-n-slate-11"
          >
            <input
              type="checkbox"
              :checked="businessDays.includes(day.value)"
              @change="toggleDay(day.value)"
            />
            {{ day.label }}
          </label>
        </div>
        <p class="text-xs text-n-slate-10">
          {{ t('KANBAN.SETTINGS_PAGE.SCHEDULE_HINT') }}
        </p>
      </div>
      <div class="flex flex-col gap-3">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.SETTINGS_PAGE.PAUSED_STEPS') }}
        </h2>
        <p class="text-xs text-n-slate-10">
          {{ t('KANBAN.SETTINGS_PAGE.PAUSED_STEPS_HINT') }}
        </p>
        <label
          v-for="step in boardSteps"
          :key="step.id"
          class="flex items-center gap-2 text-sm text-n-slate-11"
        >
          <input
            type="checkbox"
            :checked="pausedStepIds.includes(step.id)"
            @change="togglePausedStep(step.id)"
          />
          {{ step.name }}
        </label>
      </div>
      <div class="flex justify-end">
        <Button
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="saveSettings"
        >
          {{ t('KANBAN.SETTINGS_PAGE.SAVE') }}
        </Button>
      </div>
    </div>
  </SettingsLayout>
</template>
